import { Avatar, Tooltip } from "@mui/material";
import React, { useState, useEffect } from "react";
import { getAvatarColor, getInitials } from "../utils/avatarColors";

// The tint only ever shows through when the image is absent or fails to load,
// so it is applied unconditionally rather than branching on `src`.
const getAvatarProps = (name, style) => {
    const initials = getInitials(name);
    const { bg, fg } = getAvatarColor(name);
    return {
        sx: {
            bgcolor: bg,
            color: fg,
            width: "2.5rem",
            height: "2.5rem",
            fontSize: "1rem",
            fontWeight: 500,
            borderRadius: "50%",
            cursor: "pointer",
            position: "relative",
            ...style
        },
        // An empty string still counts as children to MUI, which then paints an
        // empty circle; undefined lets it fall back to the person glyph.
        children: initials || undefined,
    };
};

// Rows routinely carry no image at all, and `undefined + ""` stringifies to a
// bogus "undefined" URL that costs a request and a blank circle before it 404s.
const isUsableSrc = (src) =>
    Boolean(src) && String(src).length > 0 && !String(src).includes("undefined");

function ImageAvatar({ src, alt, width = "2.5rem", height = "2.5rem", style, tooltip = false, toolTipPlacement = "bottom", updatedDate = null }) {
    const usable = isUsableSrc(src);
    const resolvedSrc = usable ? src + (updatedDate ? `?cacheBuster=${updatedDate}` : "") : null;

    // MUI paints the <img> as soon as `src` is set and leaves the circle blank
    // until the bytes arrive, so a slow avatar reads as a missing one even
    // though the name was ready all along. Decoding the image off to the side
    // first means initials render immediately and the photo swaps in only once
    // it can actually be drawn. The fetch starts at the same moment either way,
    // so nothing is delayed by this — only the empty state is avoided.
    const [loadedSrc, setLoadedSrc] = useState(null);

    useEffect(() => {
        if (!resolvedSrc) {
            setLoadedSrc(null);
            return undefined;
        }

        let cancelled = false;
        const image = new Image();
        image.onload = () => {
            if (!cancelled) setLoadedSrc(resolvedSrc);
        };
        image.onerror = () => {
            if (!cancelled) setLoadedSrc(null);
        };
        image.src = resolvedSrc;

        return () => {
            cancelled = true;
        };
    }, [resolvedSrc]);

    const avatar = <Avatar src={loadedSrc || undefined} {...getAvatarProps(alt, style)} />;

    return (
        <div style={{ position: "relative", display: "inline-block", width, height }}>
            {/* Avatar Background (Always Visible) */}
            {tooltip ? (
                <Tooltip title={alt} placement={toolTipPlacement}>
                    {avatar}
                </Tooltip>
            ) : (
                avatar
            )}
        </div>
    );
}

export default ImageAvatar;
